import React from 'react';
import { format, parse, parseISO, isFirstDayOfMonth, getDate } from 'date-fns';
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
  customTooltip: {
    background: "#e9e9e9",
    padding: "6px 12px",
    border: "1px solid",
    borderRadius: "3px",
  },
  toolTipLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "& p": {
      margin: "0px !important",
    },
  },
  colorBox: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid transparent",
  },
  label: {
    margin: 0,
  },
  clickableElement: {
    cursor: 'pointer',
  },
  xAxisMonthLabel: {
    fontWeight: 'bold',
    fill: '#333',
    fontSize: '18px',
  },
  xAxisDayTick: {
    fill: '#666',
    fontSize: '16px',
  },
}));

// Helper function to truncate text and add ellipsis
const truncateText = (text, maxLength = 20) => {
  if (!text || typeof text !== 'string') return { text, isTruncated: false };
  const isTruncated = text.length > maxLength;
  return {
    text: isTruncated ? `${text.substring(0, maxLength)}...` : text,
    isTruncated,
    originalText: text
  };
};

// Custom tick component for handling multiline labels in horizontal mode
const MultilineYAxisTick = (props) => {
  const { x, y, payload } = props;
  const lines = String(payload.value).split('\n');
  
  // Calculate vertical adjustment based on number of lines
  const lineHeight = 16;
  const totalHeight = (lines.length - 1) * lineHeight;
  
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => {
        const { text: truncatedLine, isTruncated, originalText } = truncateText(line, 22);
        return (
          <text 
            key={index}
            x={-10} 
            y={(index * lineHeight) - (totalHeight / 2)} // Adjust positioning relative to center
            textAnchor="end" 
            fill="#666"
            title={isTruncated ? originalText : undefined}
          >
            {truncatedLine}
          </text>
        );
      })}
    </g>
  );
};

// Consolidated Tick Renderer Function
const renderCategoryAxisTick = (props, timeGrouping, isDateAxis, classes) => {
  const { x, y, payload, index, ticks } = props;
  const value = payload.value;

  // Handle detailed date axis (previously renderDateAxisTicks from AxisUtils)
  if (isDateAxis && typeof value === 'number') {
    const date = new Date(value);
    const day = date.getDate();
    const isMonthStart = day === 1;

    if (isMonthStart) {
      // Month start: Angled label MM YY
      const dateText = date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      const { text, isTruncated, originalText } = truncateText(dateText);
      return (
        <text
          x={x}
          y={y + 18} // Adjust positioning as needed
          transform={`rotate(-45, ${x}, ${y + 18})`}
          textAnchor="end"
          className={classes.xAxisMonthLabel}
          title={isTruncated ? originalText : undefined}
        >
          {text}
        </text>
      );
    } else {
      // Regular days: day number
      return (
        <text
          x={x}
          y={y + 12} // Adjust positioning as needed
          textAnchor="middle"
          className={classes.xAxisDayTick}
        >
          {day}
        </text>
      );
    }
  }

  // Handle 'Daily' time grouping with custom rendering (previously getCustomXAxisProps)
  if (timeGrouping === 'Daily' && typeof value === 'string' && value.includes('-')) {
     try {
        const date = parseISO(value);
        const isFirstDay = isFirstDayOfMonth(date) || index === 0;

        return (
          <g transform={`translate(${x},${y})`}>
            <text
              x={0}
              y={0}
              dy={16}
              textAnchor="middle"
              fill="#666"
            >
              {getDate(date)}
            </text>
            {isFirstDay && (
              <text
                x={0}
                y={0}
                dy={36}
                textAnchor="middle"
                fill="#666"
                fontWeight="bold"
              >
                {truncateText(format(date, 'MMM')).text}
              </text>
            )}
          </g>
        );
      } catch (e) {
        // Fallback for parsing error
        const { text, isTruncated, originalText } = truncateText(value);
        return (
          <g transform={`translate(${x},${y})`}>
            <text 
              x={0} 
              y={0} 
              dy={16} 
              textAnchor="middle" 
              fill="#666"
              title={isTruncated ? originalText : undefined}
            >
              {text}
            </text>
          </g>
        );
      }
  }

  // Handle general formatting based on timeGrouping (previously formatXAxisTick)
  let formattedTick = value; // Default to original value

  if (typeof value === 'string') {
     if (value.includes('-')) { // ISO Date String like YYYY-MM-DD...
      try {
        const date = parseISO(value);
        switch (timeGrouping) {
          case 'Hourly':
            formattedTick = format(date, 'HH:mm, MMM d'); break;
          // Daily case handled above, but keep Monthly/Weekly here
          case 'Weekly':
            formattedTick = format(date, "'Week of' MMM d, yyyy"); break;
          case 'Monthly':
          default:
             // For monthly, only show month on first day of month or first tick
            if (isFirstDayOfMonth(date) || index === 0) {
              formattedTick = format(date, 'MMM yyyy');
            } else {
              // Show abbreviated month for subsequent ticks in monthly view
              formattedTick = format(date, 'MMM'); 
      }
            break;
        }
      } catch (e) { /* Use default formattedTick */ }
    } else if (value.includes('/')) { // Legacy M/yyyy format
    try {
      const date = parse(value, 'M/yyyy', new Date());
        formattedTick = format(date, 'MMM yyyy');
      } catch (e) { /* Use default formattedTick */ }
    }
  } else if (typeof value === 'number') {
     // Fallback for numeric values (like timestamps not caught by isDateAxis)
     // This might need refinement based on actual data patterns
     try {
        const date = new Date(value);
         // Basic date format as a fallback
        formattedTick = format(date, 'MMM d, yyyy');
     } catch (e) { /* Use default formattedTick */ }
  }

  // Apply truncation to the formatted tick text
  const { text: truncatedText, isTruncated, originalText } = truncateText(formattedTick);
  formattedTick = truncatedText;

  // Filter out unknown props before spreading
  const { verticalAnchor, visibleTicksCount, ...restTickProps } = props || {};

  // Default text rendering
  if (restTickProps.angle) {
    // If angle is specified (e.g., -45), apply rotation transform
    return (
      <text
        {...restTickProps} // Spread props first
        transform={`rotate(${restTickProps.angle}, ${restTickProps.x}, ${restTickProps.y})`} // Apply rotation
        // textAnchor should be correctly set by {...restTickProps}
        fill="#666"
        title={isTruncated ? originalText : undefined}
      >
        {formattedTick}
      </text>
    );
  } else {
    // If no angle, render normally (textAnchor set by props)
    return (
      <text 
        {...restTickProps} 
        fill="#666"
        title={isTruncated ? originalText : undefined}
      >
        {formattedTick}
      </text>
    );
  }
};

/**
 * Hook for common chart formatting utilities
 * @param {string} timeGrouping - The time grouping type ('Hourly', 'Daily', 'Weekly', 'Monthly')
 * @param {boolean} isDateAxis - Flag indicating if the category axis uses detailed date/time rendering
 * @returns {Object} Formatting utilities for charts
 */
const useChartFormatting = (timeGrouping = 'Monthly', isDateAxis = false) => {
  const { classes } = useStyles();

  /**
   * Format a date for tooltip display
   * @param {string} label - The date label to format
   * @returns {string} Formatted date for tooltip
   */
  const formatTooltipDate = (label) => {
    let formattedDate = label;
    
    try {
      if (label.includes('-')) {
        const date = parseISO(label);
        switch (timeGrouping) {
          case 'Hourly':
            formattedDate = format(date, 'HH:mm, MMMM d, yyyy');
            break;
          case 'Daily':
            formattedDate = format(date, 'MMMM d, yyyy');
            break;
          case 'Weekly':
            formattedDate = format(date, "'Week of' MMMM d, yyyy");
            break;
          case 'Monthly':
            formattedDate = format(date, 'MMMM yyyy');
            break;
          default:
            formattedDate = label;
        }
      } else if (label.includes('/')) {
        const date = parse(label, 'M/yyyy', new Date());
        formattedDate = format(date, 'MMMM yyyy');
      }
    } catch (e) {
      // If parsing fails, use the original label
      formattedDate = label;
    }
    
    return formattedDate;
  };

  /**
   * Custom tooltip component for charts
   */
  const CustomTooltip = ({ active, payload, label, showToolTip, legendItems }) => {
    if (active && payload && payload.length && showToolTip) {
      // Use displayDate if available, otherwise use formatted label
      const formattedDate = payload[0]?.payload?.displayDate || formatTooltipDate(label);
      
      // Check if there are defects to display
      const hasDefects = payload[0]?.payload?.defects && payload[0]?.payload?.defects.length > 0;
      const defects = hasDefects ? payload[0]?.payload?.defects : [];
      
      return (
        <div className={classes.customTooltip}>
          <b className={classes.label} style={{ display: 'block', marginBottom: '8px' }}>
            {formattedDate}
          </b>
          {payload[0]?.payload?.siteAssetName && (
            <div className={classes.toolTipLabel} style={{ marginBottom: '6px' }}>
              <label style={{ fontSize: '14px' }}>
                Asset:
                <span 
                  style={{ fontWeight: 'bold', marginLeft: '4px' }}
                  title={payload[0]?.payload?.siteAssetName}
                >
                  {truncateText(payload[0]?.payload?.siteAssetName, 30).text}
                </span>
              </label>
            </div>
          )}
          {payload.map((entry, index) => {
            // Try to get the label from legendItems if available
            const itemLabel = legendItems && legendItems[index] 
              ? legendItems[index].label 
              : entry.name;
            
            const { text: truncatedLabel, originalText: fullLabel } = truncateText(itemLabel, 30);
              
            return (
              <div key={index} className={classes.toolTipLabel}>
                <div
                  className={classes.colorBox}
                  style={{ 
                    background: entry.fill || entry.stroke,
                    borderColor: entry.stroke || entry.fill,
                  }}
                />
                <label style={{ fontSize: '14px' }}>
                  <span title={fullLabel}>{truncatedLabel}:</span>
                  <span style={{ fontWeight: 'bold', marginLeft: '4px' }}>{entry.value}</span>
                </label>
              </div>
            );
          })}
          
          {/* Display defects if present */}
          {hasDefects && (
            <>
              <div style={{ marginTop: '8px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
                <b style={{ display: 'block', marginBottom: '6px' }}>Defects:</b>
                {defects.map((defect, index) => {
                  const { text: truncatedDetail, originalText: fullDetail } = truncateText(defect.defectDetail, 40);
                  const { text: truncatedRiskRating, originalText: fullRiskRating } = truncateText(defect.defectRiskRatingName, 15);
                  const { text: truncatedTypeName, originalText: fullTypeName } = truncateText(defect.defectTypeName, 15);
                  const { text: truncatedStatusName, originalText: fullStatusName } = truncateText(defect.defectStatusName, 15);
                  
                  return (
                    <div key={index} style={{ marginBottom: '4px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          backgroundColor: '#FF0000',
                          marginTop: '5px',
                          marginRight: '6px'
                        }}/>
                        <div>
                          <div><b>#{defect.number}</b> - <span title={fullDetail}>{truncatedDetail}</span></div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            <span title={fullRiskRating}>{truncatedRiskRating}</span> • 
                            <span title={fullTypeName}>{truncatedTypeName}</span> • 
                            <span title={fullStatusName}>{truncatedStatusName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // Expose the consolidated tick renderer
  const renderTick = (props) => renderCategoryAxisTick(props, timeGrouping, isDateAxis, classes);

  return {
    formatTooltipDate,
    CustomTooltip,
    renderTick,
    truncateText,
    classes,
    MultilineYAxisTick
  };
};

export default useChartFormatting; 